import { z } from 'zod';
import { StatusTypeSchema } from '../enums/status.js';
import { authDetailsSchema, authSchema } from './auth.js';
import { HintItemSchema } from './hint-items.js';

export const greetingInitialMessageConfigSchema = z.object({
  type: z.literal('greeting-starter-questions'),
  message: z.string().nullable(),
  starterItems: z.array(HintItemSchema).default([]),
});

export const hcpSelectionInitialMessageConfigSchema = z.object({
  type: z.literal('hcp-selection'),
  message: z.string().nullable(),
});

export const markdownInitialMessageConfigSchema = z.object({
  type: z.literal('markdown'),
  message: z.string().nullable(),
});

export const frmAccountSearchInitialMessageConfigSchema = z.object({
  type: z.literal('frm-account-search'),
  message: z.string().nullable(),
});

export const repTerritoryInsightsInitialMessageConfigSchema = z.object({
  type: z.literal('rep-territory-insights'),
  message: z.string().nullable(),
});

export const initialMessageConfigSchema = z.discriminatedUnion('type', [
  greetingInitialMessageConfigSchema,
  hcpSelectionInitialMessageConfigSchema,
  markdownInitialMessageConfigSchema,
  frmAccountSearchInitialMessageConfigSchema,
  repTerritoryInsightsInitialMessageConfigSchema,
]);
export type InitialMessageConfig = z.infer<typeof initialMessageConfigSchema>;

const defaultInitialMessageConfig: InitialMessageConfig = {
  type: 'greeting-starter-questions',
  message: null,
  starterItems: [],
};

export const chatAutocompleteDisplayConfigSchema = z.object({
  /** Label shown above the dropdown, e.g. "Accounts in your territory" */
  headerLabel: z.string().optional(),
});
export type ChatAutocompleteDisplayConfig = z.infer<typeof chatAutocompleteDisplayConfigSchema>;

export const connectedDataSourceSchema = z.object({
  /** Unique identifier for this data source within a context */
  dataSourceId: z.string(),
  displayName: z.string(),
  baseUrl: z.string().url(),
  auth: authSchema,
});
export type ConnectedDataSource = z.infer<typeof connectedDataSourceSchema>;

export const subjectAutocompleteDataSchema = z
  .object({
    /** Whether the header subject autocomplete search is enabled for this context */
    headerAutocomplete: z.boolean().default(true),
    /** Whether the @ mention chat autocomplete is enabled for this context */
    chatAutocompleteEnabled: z.boolean().default(false),
    /** Reference to a ConnectedDataSource providing the search endpoint and auth */
    dataSourceId: z.string().optional(),
    /** Inline base URL when not using a named ConnectedDataSource */
    baseUrl: z.string().optional(),
    /** Relative path on the base URL, e.g. "/api/subjects/search" */
    path: z.string().startsWith('/').optional(),
    /** Query parameter name for the search string */
    queryParam: z.string().optional(),
    required: z.boolean(),
    /** White-label display customization for the @ mention dropdown */
    displayConfig: chatAutocompleteDisplayConfigSchema.optional(),
    /**
     * @deprecated Replaced by dataSourceId + path or baseUrl + path.
     * Kept for backward compatibility with existing records.
     */
    endpoint: z.string().optional(),
    /**
     * @deprecated Auth is now configured on ConnectedDataSource.
     * Kept for backward compatibility with existing records.
     */
    auth: authSchema.optional(),
  })
  .refine(
    (data) => data.dataSourceId !== undefined || data.baseUrl !== undefined || data.endpoint !== undefined,
    { message: 'Either dataSourceId, baseUrl, or endpoint must be provided' }
  );
export type SubjectAutocompleteData = z.infer<typeof subjectAutocompleteDataSchema>;

export const chatContextLayoutSchema = z.enum(['full_page', 'side_chat']);
export type ChatContextLayout = z.infer<typeof chatContextLayoutSchema>;

// Schema with detailed auth data
export const subjectAutocompleteDetailsSchema = z
  .object({
    /** Whether the header subject autocomplete search is enabled for this context */
    headerAutocomplete: z.boolean().default(true),
    /** Whether the @ mention chat autocomplete is enabled for this context */
    chatAutocompleteEnabled: z.boolean().default(false),
    /** Reference to a ConnectedDataSource providing the search endpoint and auth */
    dataSourceId: z.string().optional(),
    /** Resolved name of the DataConnection referenced by dataSourceId */
    dataSourceName: z.string().optional(),
    /** Inline base URL when not using a named ConnectedDataSource */
    baseUrl: z.string().optional(),
    /** Relative path on the base URL, e.g. "/api/subjects/search" */
    path: z.string().startsWith('/').optional(),
    /** Query parameter name for the search string */
    queryParam: z.string().optional(),
    required: z.boolean(),
    /** White-label display customization for the @ mention dropdown */
    displayConfig: chatAutocompleteDisplayConfigSchema.optional(),
    /**
     * @deprecated Replaced by dataSourceId + path or baseUrl + path.
     * Kept for backward compatibility with existing records.
     */
    endpoint: z.string().optional(),
    /**
     * @deprecated Auth is now configured on ConnectedDataSource.
     * Kept for backward compatibility with existing records.
     */
    auth: authDetailsSchema.optional(),
  })
  .refine(
    (data) => data.dataSourceId !== undefined || data.baseUrl !== undefined || data.endpoint !== undefined,
    { message: 'Either dataSourceId, baseUrl, or endpoint must be provided' }
  );
export type SubjectAutocompleteDetails = z.infer<typeof subjectAutocompleteDetailsSchema>;

export const chatContextTableItemSchema = z.object({
  id: z.coerce.number(),
  identifier: z.string(),
  name: z.string(),
  status: StatusTypeSchema,
  primaryAgent: z.object({
    id: z.coerce.number(),
    name: z.string(),
  }),
  applications: z.array(
    z.object({
      id: z.coerce.number(),
      name: z.string(),
    })
  ),
  metadata: z.object({
    applications: z.number(),
  }),
});
export type ChatContextTableItem = z.infer<typeof chatContextTableItemSchema>;

export const chatContextDataSchema = z.object({
  chatNameTemplate: z.string(),
  chatNameGenerationPromptId: z.number().nullable(),
  suggestedFollowupPromptIdentifier: z.uuid().nullable(),
  createdOn: z.coerce.date(),
  id: z.coerce.number(),
  identifier: z.string(),
  name: z.string(),
  status: StatusTypeSchema,
  primaryAgent: z.object({
    id: z.coerce.number(),
    identifier: z.string(),
    name: z.string(),
  }),
  copilotsToChatContexts: z
    .array(z.object({ copilotId: z.number(), copilotIdentifier: z.string().optional() }))
    .optional(),
  initialMessageConfig: initialMessageConfigSchema.default(defaultInitialMessageConfig),
  additionalAgentContext: z.record(z.string(), z.unknown()),
  subjectAutocomplete: subjectAutocompleteDataSchema.nullable(),
  showLandingCardsOnNewChat: z.boolean().prefault(false),
  hideInputOnContextOpen: z.boolean().prefault(false),
});
export type ChatContextData = z.infer<typeof chatContextDataSchema>;

export const chatContextDetailsSchema = z.object({
  chatNameTemplate: z.string(),
  chatNameGenerationPrompt: z.object({ id: z.number(), identifier: z.string(), name: z.string() }).nullable(),
  suggestedFollowupPrompt: z.object({ id: z.number(), identifier: z.string(), name: z.string() }).nullable(),
  createdOn: z.coerce.date(),
  id: z.coerce.number(),
  identifier: z.string(),
  name: z.string(),
  status: StatusTypeSchema,
  primaryAgent: z.object({
    id: z.coerce.number(),
    identifier: z.string(),
    name: z.string(),
  }),
  copilotsToChatContexts: z
    .array(z.object({ copilotId: z.number(), copilotIdentifier: z.string().optional() }))
    .optional(),
  initialMessageConfig: initialMessageConfigSchema.default(defaultInitialMessageConfig),
  additionalAgentContext: z.record(z.string(), z.unknown()),
  subjectAutocomplete: subjectAutocompleteDetailsSchema.nullable(),
  showLandingCardsOnNewChat: z.boolean().prefault(false),
  hideInputOnContextOpen: z.boolean().prefault(false),
});
export type ChatContextDetails = z.infer<typeof chatContextDetailsSchema>;
